import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { LicenseStatus } from "../sync/types";
import { locale, t, tn } from "../i18n";

// licenseMessage says what the license's state means, in the reader's
// language; the server's message is for its logs.
export function licenseMessage(lic: LicenseStatus) {
  const l = lic.license;
  const date = l ? new Date(l.expires_at).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" }) : "";
  switch (lic.state) {
    case "unlicensed":
      return t("No license is installed. Lantern works fully; install the one your vendor sent.");
    case "invalid":
      return t("The installed license is not valid for this build. Install the one your vendor sent.");
    case "expired":
      return t("The license expired on {date}. Messaging goes on; renew it.", { date });
    case "over_seats":
      return t("{used} people are active, over the license's {seats} seats.", { used: lic.used, seats: lic.seats });
    case "expiring":
      return tn(lic.days_left, "The license expires in {n} day, on {date}.", "The license expires in {n} days, on {date}.", { date });
    default:
      return t("Licensed to {customer} for {seats} seats until {date}.", { customer: l?.customer ?? "", seats: lic.seats, date });
  }
}

// licenseChanged is the event the System page sends once it installs a
// license, so the banner shows the new one at once.
export const licenseChanged = "lantern:license";

// LicenseBanner tells admins when the license needs attention: missing,
// expiring, expired, over its seats or not valid. It never blocks anything.
export function LicenseBanner() {
  const session = useSession();
  const state = useLantern();
  const [lic, setLic] = useState<LicenseStatus | null>(null);
  const admin = state.me?.role === "admin";
  useEffect(() => {
    if (!admin) return;
    const load = () => session.api.license().then(setLic, () => {});
    void load();
    const timer = setInterval(load, 3_600_000);
    addEventListener(licenseChanged, load);
    return () => {
      clearInterval(timer);
      removeEventListener(licenseChanged, load);
    };
  }, [session, admin]);
  if (!lic || lic.state === "ok") return null;
  return (
    <p className="banner license-banner">
      {licenseMessage(lic)} <button onClick={() => navigate("/admin/system")}>{lic.state === "unlicensed" ? t("Install license") : t("Manage license")}</button>
    </p>
  );
}
